import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../Reducer/SnackbarReducer';
import { showErrorDialog, closeErrorDialog } from '../Reducer/ErrorDialogReducer';

/**
 * 에러 처리 훅 (전역 ErrorDialog 사용)
 * - 에러 발생 시 자동으로 분류 및 처리
 * - 401 에러는 전역 다이얼로그 표시
 * - 기타 에러는 스낵바로 표시
 * 
 * @returns {Object} { handleError, clearError }
 */
export default function useErrorHandler() {
  const dispatch = useDispatch();

  /**
   * 에러 처리
   * @param {Error|Object} error - 에러 객체 (Common.js에서 throw한 커스텀 에러)
   * @param {Object} config - axios config (선택, 이미 error.errorInfo에 포함되어 있음)
   */
  const handleError = useCallback(async (error, config = {}) => {
    try {
      // Common.js에서 이미 분류된 에러 정보 사용
      const errorInfo = error.errorInfo || {
        type: 'UNKNOWN_ERROR',
        userMessage: error.message || '오류가 발생했습니다.',
      };

      if (error.shouldShowDialog || errorInfo.type === 'AUTH_ERROR') {
        // 401 에러는 전역 다이얼로그 표시
        dispatch(
          showErrorDialog({
            title: '세션 만료',
            message: errorInfo.userMessage,
            type: 'auth',
            showLoginButton: true,
          })
        );
      } else {
        // 기타 에러는 스낵바로 표시
        dispatch(
          showSnackbar({
            message: errorInfo.userMessage,
            type: errorInfo.type === 'SERVER_ERROR' || errorInfo.type === 'NETWORK_ERROR'
              ? 'error'
              : 'warning',
          })
        );
      }
    } catch (handlerError) {
      // 에러 처리 중 에러 발생 시 기본 메시지 표시
      console.error('에러 처리 실패:', handlerError);
      dispatch(
        showSnackbar({
          message: '오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
          type: 'error',
        })
      );
    }
  }, [dispatch]);

  /**
   * 에러 다이얼로그 닫기
   */
  const clearError = useCallback(() => {
    dispatch(closeErrorDialog());
  }, [dispatch]);

  return {
    handleError,
    clearError,
  };
}

